import { motion } from "framer-motion";
import Logo from "@/components/Logo";

/**
 * Sample Bearing Document — full editorial confidential memo mockup.
 * Editorial document feel — black border, mono labels, no rounded corners.
 */
const dimensionScores = [
  ["Market opportunity", 88],
  ["Competitor vulnerability", 79],
  ["Brand readiness", 71],
  ["Financial viability", 84],
  ["Operational readiness", 76],
  ["Irish advantage", 95],
];

const marketIndex = [
  ["DE", "Germany", 92],
  ["FR", "France", 74],
  ["NL", "Netherlands", 58],
  ["IT", "Italy", 41],
];

const projections = [
  { tag: "Conservative", rev: "€180k", net: "€42k", margin: "23%" },
  { tag: "Base case", rev: "€280k", net: "€74k", margin: "26%", emphasised: true },
  { tag: "Optimistic", rev: "€420k", net: "€118k", margin: "28%" },
];

const risks = [
  {
    icon: "⚠",
    tone: "warn",
    t: "EUIPO trademark not filed",
    d: "Amazon Brand Registry requires registered EU trademark. EUIPO standard route is six months — filing immediately runs in parallel with the launch but the window is tight.",
  },
  {
    icon: "⚠",
    tone: "warn",
    t: "German labelling requirements",
    d: "Mandatory elements include responsible person, batch identifier, INCI ordering, and CLP symbols in German. Coordinate with packaging supplier before the next print run.",
  },
  {
    icon: "⚠",
    tone: "warn",
    t: "UK reviews cannot be ported",
    d: "Amazon EU treats DE as a new locale. Plan for Amazon Vine on launch SKUs to seed reviews — budget €600 per ASIN.",
  },
  {
    icon: "✓",
    tone: "good",
    t: "No import duty risk",
    d: "Ireland → Germany inside the customs union. €0 duty exposure. UK competitors carry €2–5k per market per year in fiscal representation.",
  },
];

function Row({ label, value, last = false }) {
  return (
    <div className={`grid grid-cols-12 gap-3 py-4 ${last ? "" : "border-b border-line"} items-baseline`}>
      <span className="col-span-7 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-ink-muted">
        {label}
      </span>
      <span className="col-span-5 text-right font-mono text-[12px] sm:text-[13px] text-ink">
        {value}
      </span>
    </div>
  );
}

export default function BearingSampleDoc() {
  return (
    <section className="bg-surface border-b border-line" data-testid="bearing-sample-doc">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <div className="mb-10 sm:mb-14">
          <p className="label-mono">/ The document</p>
          <h2 className="mt-8 sm:mt-10 font-serif-i text-[36px] sm:text-[44px] lg:text-[56px] text-ink leading-[1.05] tracking-tight max-w-4xl">
            What a Bearing looks like.
          </h2>
          <p className="mt-8 max-w-xl text-ink-soft leading-[1.7] text-[16px] sm:text-lg">
            A worked example — illustrative numbers for an Irish natural skincare brand. Yours
            will be assembled differently. Every Bearing is bespoke.
          </p>
        </div>

        {/* ───── The document ───── */}
        <div className="bg-paper border border-ink shadow-[0_1px_0_#DDD8CE]">
          {/* TOP STRIP */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 sm:px-10 py-5 border-b border-ink">
            <div className="flex items-center gap-4">
              <Logo size="sm" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted hidden sm:inline">
                · Brand Bearing
              </span>
            </div>
            <div className="flex items-center gap-4 font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted">
              <span>Ref · TRV-2026-014</span>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">Prepared June 2026</span>
              <span className="text-coral">· Confidential</span>
            </div>
          </div>

          {/* TITLE */}
          <div className="px-6 sm:px-10 pt-10 pb-8 border-b border-line">
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-ink-muted">
              Amazon EU · Market Assessment
            </p>
            <h3 className="mt-5 font-serif-i text-[40px] sm:text-[56px] lg:text-[64px] text-ink leading-[1.02] tracking-tight">
              Irish Skincare Brand
            </h3>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-6">
              {[
                ["Brand", "Irish Skincare Brand"],
                ["Category", "Natural Skincare"],
                ["Priority market", "Amazon.de → .fr"],
                ["Prepared by", "Traeva · Dublin"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-subtle">
                    {k}
                  </p>
                  <p className="mt-3 text-[14px] sm:text-[15px] text-ink leading-snug">{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* VERDICT BAND */}
          <div className="grid grid-cols-1 lg:grid-cols-3 border-b border-line">
            <div className="px-6 sm:px-10 py-10 border-b lg:border-b-0 lg:border-r border-line">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                Bearing verdict
              </p>
              <div className="mt-6 flex items-center gap-4">
                <span className="w-2 h-2 bg-coral inline-block" />
                <span className="font-serif-i text-[64px] sm:text-[72px] text-coral leading-none">
                  GO.
                </span>
              </div>
            </div>
            <div className="px-6 sm:px-10 py-10 border-b lg:border-b-0 lg:border-r border-line">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                Confidence
              </p>
              <p className="mt-6 font-serif-i text-[40px] sm:text-[48px] text-ink leading-none">
                High
              </p>
            </div>
            <div className="px-6 sm:px-10 py-10">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                Composite score
              </p>
              <p className="mt-6 font-serif-i text-[40px] sm:text-[48px] text-ink leading-none">
                82<span className="text-ink-muted text-[28px] sm:text-[32px]"> / 100</span>
              </p>
            </div>
          </div>

          {/* DIMENSION SCORES + MARKET INDEX */}
          <div className="grid grid-cols-1 lg:grid-cols-2 border-b border-line">
            <div className="px-6 sm:px-10 py-10 border-b lg:border-b-0 lg:border-r border-line">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                Dimension scores
              </p>
              <div className="mt-7">
                {dimensionScores.map(([k, v], i) => (
                  <Row key={k} label={k} value={`${v} / 100`} last={i === dimensionScores.length - 1} />
                ))}
              </div>
            </div>

            <div className="px-6 sm:px-10 py-10">
              <div className="flex items-baseline justify-between">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                  EU market opportunity
                </p>
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-subtle">
                  Indexed · 0–100
                </p>
              </div>
              <ul className="mt-7 space-y-5">
                {marketIndex.map(([code, name, w], i) => (
                  <li key={code} className="grid grid-cols-12 items-center gap-4">
                    <span className="col-span-2 font-mono text-[11px] tracking-[0.18em] text-ink-soft">
                      {code}
                    </span>
                    <div className="col-span-7 h-[3px] bg-line relative overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${w}%` }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.1 }}
                        className="absolute inset-y-0 left-0 bg-navy"
                      />
                    </div>
                    <span className="col-span-3 font-mono text-[11px] text-ink text-right">
                      {w}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-7 font-mono text-[10px] tracking-[0.2em] uppercase text-ink-subtle">
                Based on Helium 10 category data · June 2026
              </p>
            </div>
          </div>

          {/* CRITICAL INSIGHT */}
          <div className="px-6 sm:px-10 py-10 sm:py-14 border-b border-line">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-coral">
              Critical insight
            </p>
            <p className="mt-6 font-serif-i text-[22px] sm:text-[28px] lg:text-[32px] text-ink leading-[1.25] max-w-4xl">
              “Irische Naturkosmetik” — 6,200 monthly searches on Amazon.de. Zero Irish natural
              skincare brands rank in the top 20. The €22–€28 price band has two incumbents,
              both with fewer than 200 reviews and weak A+ content. This keyword cluster is
              undefended. It is your first 90 days on Amazon EU.
            </p>
          </div>

          {/* PROJECTIONS */}
          <div className="px-6 sm:px-10 py-10 border-b border-line">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
              Year 1 projection
            </p>
            <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
              {projections.map((p) => (
                <div
                  key={p.tag}
                  data-testid={`projection-${p.tag.toLowerCase().replace(/\s/g, "-")}`}
                  className={`p-7 ${p.emphasised ? "bg-navy text-paper" : "bg-paper text-ink"}`}
                >
                  <p
                    className={`font-mono text-[10px] tracking-[0.22em] uppercase ${p.emphasised ? "text-paper/55" : "text-ink-muted"}`}
                  >
                    {p.tag}
                  </p>
                  <p
                    className={`mt-7 font-serif-i text-[44px] sm:text-[52px] leading-none ${p.emphasised ? "text-coral" : "text-ink"}`}
                  >
                    {p.rev}
                  </p>
                  <p
                    className={`mt-4 text-[13px] sm:text-[14px] leading-relaxed ${p.emphasised ? "text-paper/85" : "text-ink-soft"}`}
                  >
                    Net to brand · <span className={`font-mono ${p.emphasised ? "text-paper" : "text-ink"}`}>{p.net}</span> · {p.margin}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* RISK REGISTER */}
          <div className="px-6 sm:px-10 py-10 sm:py-14 border-b border-line">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-coral">
              Risk register · specific to this brand
            </p>
            <ul className="mt-8 border-t border-line">
              {risks.map((r, i) => (
                <li
                  key={i}
                  data-testid={`risk-${i}`}
                  className="grid grid-cols-12 gap-4 sm:gap-6 py-7 border-b border-line"
                >
                  <span
                    className={`col-span-2 sm:col-span-1 font-mono text-[14px] leading-none pt-1 ${r.tone === "good" ? "text-navy" : "text-coral"}`}
                  >
                    {r.icon}
                  </span>
                  <div className="col-span-10 sm:col-span-11">
                    <p className="font-serif-i text-[19px] sm:text-[22px] text-ink leading-snug">
                      {r.t}
                    </p>
                    <p className="mt-3 text-[14px] sm:text-[15px] text-ink-muted leading-relaxed max-w-2xl">
                      {r.d}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* RECOMMENDED NEXT STEP */}
          <div className="px-6 sm:px-10 py-10 sm:py-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
            <div className="max-w-2xl">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">
                Recommended next step
              </p>
              <p className="mt-5 font-serif-i text-[22px] sm:text-[26px] text-ink leading-snug">
                Traeva Tier 1 · EU Market Entry. Amazon.de + .fr in 8–12 weeks. Full account
                setup, EUIPO filing in week one, Brand Store, PPC architecture, 90-day launch
                management.
              </p>
            </div>
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-subtle whitespace-nowrap">
              Traeva · Dublin · hello@traeva.ie
            </p>
          </div>
        </div>

        <p className="mt-10 font-mono text-[10px] tracking-[0.22em] uppercase text-ink-subtle text-center">
          Illustrative sample · Reference TRV-2026-014 · Not a forecast
        </p>
      </div>
    </section>
  );
}
